/**
 * FullCalendar `events` kayıtlarını mobil liste görünümü için aya göre genişletir.
 * @typedef {{
 *   id: string,
 *   title: string,
 *   date: string,
 *   startMs: number,
 *   endMs: number,
 *   allDay: boolean,
 *   kind: 'weekly-meeting' | 'event',
 *   timeLabel: string,
 *   fcEventId: string,
 *   source?: string,
 *   sourceId?: string,
 *   startIso?: string,
 *   endIso?: string,
 *   preview?: {
 *     city?: string,
 *     location?: string,
 *     meetingRoomName?: string,
 *   },
 * }} AgendaInstance
 */

/**
 * @param {Record<string, unknown>} ev
 */
function readAgendaMeta(ev) {
    const ext =
        ev.extendedProps && typeof ev.extendedProps === 'object'
            ? /** @type {Record<string, unknown>} */ (ev.extendedProps)
            : {};
    const fcEventId = String(ev.id ?? ev.groupId ?? ev.title ?? '');
    let sourceId =
        ext.sourceId != null ? String(ext.sourceId).trim() : '';
    if (!sourceId) {
        if (fcEventId.startsWith('club-')) sourceId = fcEventId.slice(5);
        else if (fcEventId.startsWith('meet-')) sourceId = fcEventId.slice(5);
    }

    return {
        fcEventId,
        source: ext.source != null ? String(ext.source) : undefined,
        sourceId: sourceId || undefined,
        preview: {
            city: ext.city != null ? String(ext.city) : '',
            location: ext.location != null ? String(ext.location) : '',
            meetingRoomName:
                ext.meetingRoomName != null
                    ? String(ext.meetingRoomName)
                    : '',
        },
    };
}

/**
 * @param {Date} d
 */
function ymdFromDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * @param {string} ymd
 */
function dateFromYmd(ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/**
 * @param {string} iso
 */
function parseInstant(iso) {
    const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {number} year
 * @param {number} monthIndex
 */
function monthBounds(year, monthIndex) {
    const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
    const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
    return { start, end };
}

/**
 * @param {Date} a
 * @param {Date} b
 */
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
    return aStart <= bEnd && aEnd >= bStart;
}

/**
 * @param {string} dateYmd
 * @param {string} timeHms
 */
function combineDateAndTime(dateYmd, timeHms) {
    return new Date(`${dateYmd}T${timeHms}`);
}

/**
 * @param {Record<string, unknown>} ev
 * @param {Date} rangeStart
 * @param {Date} rangeEnd
 * @param {AgendaInstance[]} out
 */
function expandRecurringEvent(ev, rangeStart, rangeEnd, out) {
    const daysOfWeek = ev.daysOfWeek;
    if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) return;

    const recurStart = typeof ev.startRecur === 'string' ? ev.startRecur.slice(0, 10) : '';
    const recurEnd = typeof ev.endRecur === 'string' ? ev.endRecur.slice(0, 10) : '';
    const startTime = typeof ev.startTime === 'string' ? ev.startTime : '00:00:00';
    const endTime = typeof ev.endTime === 'string' ? ev.endTime : startTime;
    const title = String(ev.title || '');
    const kind =
        ev.extendedProps &&
        typeof ev.extendedProps === 'object' &&
        /** @type {{ kind?: string }} */ (ev.extendedProps).kind === 'weekly-meeting'
            ? 'weekly-meeting'
            : 'event';
    const baseId = String(ev.id ?? ev.groupId ?? title);
    const meta = readAgendaMeta(ev);

    const cursor = new Date(rangeStart);
    cursor.setHours(12, 0, 0, 0);
    const endCursor = new Date(rangeEnd);
    endCursor.setHours(12, 0, 0, 0);

    while (cursor <= endCursor) {
        const ymd = ymdFromDate(cursor);
        if (recurStart && ymd < recurStart) {
            cursor.setDate(cursor.getDate() + 1);
            continue;
        }
        if (recurEnd && ymd > recurEnd) break;

        if (daysOfWeek.includes(cursor.getDay())) {
            const startDt = combineDateAndTime(ymd, startTime);
            const endDt = combineDateAndTime(ymd, endTime);
            out.push({
                id: `${baseId}-${ymd}`,
                title,
                date: ymd,
                startMs: startDt.getTime(),
                endMs: endDt.getTime(),
                allDay: false,
                kind,
                timeLabel: '',
                ...meta,
                startIso: startDt.toISOString(),
                endIso: endDt.toISOString(),
            });
        }
        cursor.setDate(cursor.getDate() + 1);
    }
}

/**
 * @param {Record<string, unknown>} ev
 * @param {Date} rangeStart
 * @param {Date} rangeEnd
 * @param {AgendaInstance[]} out
 */
function expandTimedEvent(ev, rangeStart, rangeEnd, out) {
    const startRaw = ev.start;
    if (typeof startRaw !== 'string') return;

    const startDt = parseInstant(startRaw);
    if (!startDt) return;

    const endRaw = typeof ev.end === 'string' ? ev.end : startRaw;
    const endDt = parseInstant(endRaw) ?? startDt;
    const allDay = startRaw.length === 10 || Boolean(ev.allDay);
    const title = String(ev.title || '');
    const kind =
        ev.extendedProps &&
        typeof ev.extendedProps === 'object' &&
        /** @type {{ kind?: string }} */ (ev.extendedProps).kind === 'weekly-meeting'
            ? 'weekly-meeting'
            : 'event';
    const baseId = String(ev.id ?? title);
    const meta = readAgendaMeta(ev);

    if (allDay) {
        const startYmd = startRaw.slice(0, 10);
        let endYmd = endRaw.slice(0, 10);
        if (endYmd > startYmd) {
            const exclusiveEnd = dateFromYmd(endYmd);
            exclusiveEnd.setDate(exclusiveEnd.getDate() - 1);
            endYmd = ymdFromDate(exclusiveEnd);
        }
        const cursor = dateFromYmd(startYmd);
        const last = dateFromYmd(endYmd);
        while (cursor <= last) {
            const ymd = ymdFromDate(cursor);
            const dayStart = new Date(cursor);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(cursor);
            dayEnd.setHours(23, 59, 59, 999);
            if (rangesOverlap(dayStart, dayEnd, rangeStart, rangeEnd)) {
                out.push({
                    id: `${baseId}-${ymd}`,
                    title,
                    date: ymd,
                    startMs: dayStart.getTime(),
                    endMs: dayEnd.getTime(),
                    allDay: true,
                    kind,
                    timeLabel: '',
                    ...meta,
                    startIso: startYmd,
                    endIso: endYmd,
                });
            }
            cursor.setDate(cursor.getDate() + 1);
        }
        return;
    }

    if (!rangesOverlap(startDt, endDt, rangeStart, rangeEnd)) return;

    out.push({
        id: `${baseId}-${startDt.getTime()}`,
        title,
        date: ymdFromDate(startDt),
        startMs: startDt.getTime(),
        endMs: endDt.getTime(),
        allDay: false,
        kind,
        timeLabel: '',
        ...meta,
        startIso: startRaw,
        endIso: endRaw,
    });
}

/**
 * @param {Record<string, unknown>[]} events
 * @param {number} year
 * @param {number} monthIndex 0–11
 * @returns {AgendaInstance[]}
 */
export function expandEventsForMonth(events, year, monthIndex) {
    const { start, end } = monthBounds(year, monthIndex);
    /** @type {AgendaInstance[]} */
    const instances = [];

    for (const ev of events) {
        if (!ev || typeof ev !== 'object') continue;
        if (Array.isArray(ev.daysOfWeek)) {
            expandRecurringEvent(ev, start, end, instances);
        } else {
            expandTimedEvent(ev, start, end, instances);
        }
    }

    instances.sort((a, b) => a.startMs - b.startMs || a.title.localeCompare(b.title, 'tr'));
    return instances;
}

/**
 * @param {AgendaInstance[]} instances
 * @returns {{ date: string, dateObj: Date, events: AgendaInstance[] }[]}
 */
export function groupAgendaByDay(instances) {
    /** @type {Map<string, AgendaInstance[]>} */
    const map = new Map();

    for (const item of instances) {
        const list = map.get(item.date);
        if (list) list.push(item);
        else map.set(item.date, [item]);
    }

    return [...map.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, events]) => ({
            date,
            dateObj: dateFromYmd(date),
            events,
        }));
}
